import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { useLanguage } from '@/lib/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Scissors, Link, Download, Trash2, Clock, CheckCircle, 
  AlertCircle, Loader2, Image, Play, Youtube, Key, Copy,
  Eye, EyeOff, Plus, RefreshCw, Bot
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '@/lib/api';

export default function ClipGenerator() {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('generate');
  const [videoUrl, setVideoUrl] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [clips, setClips] = useState([]);
  const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: 5 });
  const [loading, setLoading] = useState(true);
  
  // CUA API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyCreated, setNewKeyCreated] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // YouTube cookies (server-side download auth)
  const [cookiesConfigured, setCookiesConfigured] = useState(false);
  const [showCookies, setShowCookies] = useState(false);
  const [cookiesText, setCookiesText] = useState('');
  const [savingCookies, setSavingCookies] = useState(false);

  const fetchCookiesStatus = useCallback(async () => {
    try {
      const res = await api.getYouTubeCookiesStatus();
      setCookiesConfigured(!!res.data.configured);
    } catch (e) { /* ignore */ }
  }, []);

  const handleSaveCookies = async () => {
    setSavingCookies(true);
    try {
      const res = await api.saveYouTubeCookies(cookiesText);
      setCookiesConfigured(!!res.data.configured);
      toast.success(res.data.message || 'Cookies salvos');
      setCookiesText('');
      setShowCookies(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cookies');
    } finally {
      setSavingCookies(false);
    }
  };

  const fetchClips = useCallback(async () => {
    try {
      const res = await api.getMyClips({ limit: 50 });
      setClips(res.data.clips || []);
      setDailyUsage(res.data.daily_usage || { used: 0, limit: 5 });
    } catch (error) {
      console.error('Failed to fetch clips:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoadingKeys(true);
      const res = await api.listCuaApiKeys();
      setApiKeys(res.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    fetchClips();
    fetchApiKeys();
  }, [fetchClips, fetchApiKeys]);

  // Resume tracking an in-progress job after navigating away or reopening the app
  useEffect(() => {
    fetchCookiesStatus();
    const savedId = localStorage.getItem('vc_active_clip_job');
    if (!savedId) return;
    (async () => {
      try {
        const res = await api.getClipJobStatus(savedId);
        const job = res.data;
        if (job && job.status !== 'ready' && job.status !== 'failed') {
          setCurrentJob(job);
          setProcessing(true);
        } else {
          localStorage.removeItem('vc_active_clip_job');
        }
      } catch (error) {
        localStorage.removeItem('vc_active_clip_job');
      }
    })();
  }, []);

  // Poll for job status
  useEffect(() => {
    if (!currentJob || currentJob.status === 'ready' || currentJob.status === 'failed') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const res = await api.getClipJobStatus(currentJob.id);
        setCurrentJob(res.data);
        
        // Clips are inserted one-by-one on the server — refresh the library as they appear
        if (res.data.status === 'cutting') {
          fetchClips();
        }
        
        if (res.data.status === 'ready') {
          toast.success(`${res.data.clips?.length || 0} cortes gerados com sucesso!`);
          localStorage.removeItem('vc_active_clip_job');
          fetchClips();
          setProcessing(false);
        } else if (res.data.status === 'failed') {
          toast.error(res.data.error_message || 'Falha ao processar vídeo');
          localStorage.removeItem('vc_active_clip_job');
          setProcessing(false);
        }
      } catch (error) {
        console.error('Failed to fetch job status:', error);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentJob, fetchClips]);

  const handleSubmitUrl = async (e) => {
    e.preventDefault();
    
    if (!videoUrl.trim()) {
      toast.error('Cole uma URL do YouTube');
      return;
    }

    // Validate YouTube URL
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(videoUrl)) {
      toast.error('Apenas URLs do YouTube são permitidas');
      return;
    }

    if (dailyUsage.used >= dailyUsage.limit) {
      toast.error(`Limite diário atingido (${dailyUsage.used}/${dailyUsage.limit}). Atualize seu plano.`);
      return;
    }

    setProcessing(true);
    
    try {
      const res = await api.processVideoUrl({
        url: videoUrl,
        platform: 'youtube_shorts',
        max_clips: 5,
        min_duration: 15,
        max_duration: 60
      });
      
      setCurrentJob(res.data);
      localStorage.setItem('vc_active_clip_job', res.data.id);
      setDailyUsage(res.data.daily_usage);
      toast.success('Processamento iniciado! Ele continua no servidor mesmo se você sair ou fechar o app.');
      setVideoUrl('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar vídeo');
      setProcessing(false);
    }
  };

  const handleDownload = async (clip) => {
    try {
      const response = await api.downloadClip(clip.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viral_clip_${clip.id.slice(0, 8)}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download iniciado!');
      
      // Update clip status locally
      setClips(prev => prev.map(c => 
        c.id === clip.id ? { ...c, status: 'downloaded' } : c
      ));
    } catch (error) {
      if (error.response?.status === 410) {
        toast.error('Este clip já foi baixado. Apenas a miniatura está disponível.');
      } else {
        toast.error('Erro ao baixar clip');
      }
    }
  };

  const handleDeleteClip = async (clipId) => {
    if (!window.confirm('Tem certeza que deseja deletar este clip?')) return;
    
    try {
      await api.deleteGeneratedClip(clipId);
      setClips(prev => prev.filter(c => c.id !== clipId));
      toast.success('Clip deletado');
    } catch (error) {
      toast.error('Erro ao deletar clip');
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    
    if (!newKeyName.trim()) {
      toast.error('Digite um nome para a chave');
      return;
    }

    try {
      const res = await api.createCuaApiKey({ name: newKeyName });
      setNewKeyCreated(res.data);
      setNewKeyName('');
      fetchApiKeys();
      toast.success('Chave API criada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar chave');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Tem certeza que deseja revogar esta chave?')) return;
    
    try {
      await api.revokeCuaApiKey(keyId);
      fetchApiKeys();
      toast.success('Chave revogada');
    } catch (error) {
      toast.error('Erro ao revogar chave');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      processing: { color: 'bg-blue-500', icon: Loader2, text: 'Processando' },
      ready: { color: 'bg-green-500', icon: CheckCircle, text: 'Pronto' },
      downloaded: { color: 'bg-gray-500', icon: Download, text: 'Baixado' },
      expired: { color: 'bg-red-500', icon: AlertCircle, text: 'Expirado' },
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pendente' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getCuaStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', text: 'Aguardando CUA' },
      notified: { color: 'bg-blue-500', text: 'CUA Notificado' },
      posting: { color: 'bg-purple-500', text: 'Postando...' },
      posted: { color: 'bg-green-500', text: 'Postado' },
      failed: { color: 'bg-red-500', text: 'Falhou' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge variant="outline" className={`${config.color} text-white text-xs`}>
        {config.text}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scissors className="w-6 h-6 text-primary" />
            Gerador de Clips
          </h1>
          <p className="text-muted-foreground">
            Cole uma URL do YouTube e a IA criará cortes virais automaticamente
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {dailyUsage.used}/{dailyUsage.limit} cortes hoje
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <Scissors className="w-4 h-4" />
            <span className="hidden sm:inline">Gerar Clips</span>
          </TabsTrigger>
          <TabsTrigger value="library" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            <span className="hidden sm:inline">Biblioteca</span>
          </TabsTrigger>
          <TabsTrigger value="cua" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span className="hidden sm:inline">CUA API</span>
          </TabsTrigger>
        </TabsList>

        {/* Generate Tab */}
        <TabsContent value="generate" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-500" />
                Processar Vídeo do YouTube
              </CardTitle>
              <CardDescription>
                Cole a URL de um vídeo longo. A IA vai analisar e criar cortes dos melhores momentos virais.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitUrl} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="url">URL do YouTube</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="url"
                        type="url"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        className="pl-10"
                        disabled={processing}
                        data-testid="video-url-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={processing || dailyUsage.used >= dailyUsage.limit}
                      data-testid="process-video-btn"
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Scissors className="w-4 h-4" />
                      )}
                      <span className="ml-2 hidden sm:inline">Gerar Clips</span>
                    </Button>
                  </div>
                </div>

                {/* YouTube access (cookies) */}
                <div className="mt-3 p-3 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`inline-block w-2 h-2 rounded-full ${cookiesConfigured ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      Acesso ao YouTube: {cookiesConfigured ? 'configurado' : 'não configurado'}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setShowCookies((v) => !v)}
                      data-testid="youtube-cookies-toggle"
                    >
                      {showCookies ? 'Fechar' : (cookiesConfigured ? 'Atualizar cookies' : 'Configurar cookies')}
                    </Button>
                  </div>
                  {!cookiesConfigured && (
                    <p className="text-xs text-muted-foreground mt-1">
                      O YouTube bloqueia downloads de servidores. Cole os cookies de uma conta logada para liberar, ou configure uma <strong>Conta de Download</strong> no Cofre para o Agente baixar no seu PC automaticamente.
                    </p>
                  )}
                  {showCookies && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Instale a extensão "Get cookies.txt LOCALLY", acesse youtube.com logado, exporte e cole o conteúdo abaixo (formato Netscape).
                      </p>
                      <textarea
                        className="w-full h-28 text-xs font-mono rounded-md border border-border bg-background p-2"
                        placeholder="# Netscape HTTP Cookie File ..."
                        value={cookiesText}
                        onChange={(e) => setCookiesText(e.target.value)}
                        data-testid="youtube-cookies-textarea"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveCookies} disabled={savingCookies || !cookiesText.trim()} data-testid="youtube-cookies-save">
                          {savingCookies ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar cookies'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setShowCookies(false); setCookiesText(''); }}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Processing Status */}
                {currentJob && currentJob.status !== 'ready' && currentJob.status !== 'failed' && (
                  <div className="space-y-2 p-4 bg-muted/50 rounded-lg" data-testid="clip-job-status">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {currentJob.status === 'downloading' && 'Baixando vídeo...'}
                        {currentJob.status === 'waiting_agent' && 'Aguardando o Agente baixar o vídeo no seu PC...'}
                        {currentJob.status === 'analyzing' && 'Analisando momentos virais...'}
                        {currentJob.status === 'cutting' && (
                          currentJob.clips_total
                            ? `Cortando clips... (${currentJob.clips_done || 0}/${currentJob.clips_total})`
                            : 'Cortando clips...'
                        )}
                        {currentJob.status === 'pending' && 'Iniciando...'}
                      </span>
                      <span className="text-sm text-muted-foreground">{currentJob.progress}%</span>
                    </div>
                    <Progress value={currentJob.progress} />
                    {currentJob.status === 'waiting_agent' ? (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Bot className="w-3 h-3 text-primary" />
                        O YouTube bloqueou o servidor. O Desktop Agent vai baixar no seu computador e enviar de volta — deixe o Agent rodando.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        Pode sair desta tela ou fechar o app — o processamento continua no servidor.
                      </p>
                    )}
                  </div>
                )}

                {currentJob?.status === 'failed' && (
                  <div className="p-4 bg-destructive/10 text-destructive rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {currentJob.error_message || 'Erro ao processar vídeo'}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Como Funciona</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>1. Cole a URL de um vídeo do YouTube (podcast, entrevista, etc.)</p>
              <p>2. A IA analisa o vídeo e identifica os momentos mais virais</p>
              <p>3. Clips de 15-60 segundos são gerados automaticamente</p>
              <p>4. Baixe os clips e poste nas redes sociais</p>
              <p className="text-yellow-500">
                Após o download, o vídeo é deletado. Apenas a miniatura permanece.
              </p>
              <p className="text-yellow-500">
                Clips não baixados são removidos automaticamente após 24h (sem deixar miniatura).
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Library Tab */}
        <TabsContent value="library" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Meus Clips ({clips.length})</h2>
            <Button variant="outline" size="sm" onClick={fetchClips}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : clips.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Scissors className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum clip gerado ainda</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('generate')}
                >
                  Gerar Primeiro Clip
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {clips.map((clip) => (
                <Card key={clip.id} className="overflow-hidden">
                  <div className="aspect-[9/16] bg-muted relative">
                    {clip.thumbnail_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${clip.thumbnail_url.replace('/api/clips/', '/api/generated-clips/')}`}
                        alt={clip.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Status badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      {getStatusBadge(clip.status)}
                      {clip.cua_status && getCuaStatusBadge(clip.cua_status)}
                    </div>
                    
                    {/* Duration */}
                    <div className="absolute bottom-2 right-2">
                      <Badge variant="secondary" className="bg-black/70 text-white">
                        {Math.round(clip.duration)}s
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-3 space-y-2">
                    <h3 className="font-medium text-sm line-clamp-2">{clip.title}</h3>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Score: {Math.round((clip.virality_score || 0) * 100)}%</span>
                      <span>{new Date(clip.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      {clip.status === 'ready' && (
                        <Button 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleDownload(clip)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Baixar
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeleteClip(clip.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CUA API Tab */}
        <TabsContent value="cua" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Chaves API para CUA
              </CardTitle>
              <CardDescription>
                Gere chaves API para integrar com o OpenAI Computer Use Agent (CUA).
                O CUA pode buscar clips pendentes e receber instruções de postagem.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* New Key Created Alert */}
              {newKeyCreated && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-2">
                  <p className="font-medium text-green-500">Chave criada com sucesso!</p>
                  <p className="text-sm text-muted-foreground">
                    Copie esta chave agora. Ela não será mostrada novamente.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-xs break-all">
                      {newKeyCreated.key}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(newKeyCreated.key)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setNewKeyCreated(null)}
                  >
                    Fechar
                  </Button>
                </div>
              )}

              {/* Create New Key Form */}
              {showNewKeyForm ? (
                <form onSubmit={handleCreateApiKey} className="space-y-3 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="keyName">Nome da Chave</Label>
                    <Input
                      id="keyName"
                      placeholder="Ex: CUA Desktop, Automação..."
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm">
                      Criar Chave
                    </Button>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowNewKeyForm(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <Button onClick={() => setShowNewKeyForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Chave API
                </Button>
              )}

              {/* Existing Keys List */}
              <div className="space-y-2">
                <h3 className="font-medium text-sm">Suas Chaves ({apiKeys.length}/5)</h3>
                
                {loadingKeys ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : apiKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">
                    Nenhuma chave API criada
                  </p>
                ) : (
                  <div className="space-y-2">
                    {apiKeys.map((key) => (
                      <div 
                        key={key.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="space-y-1">
                          <p className="font-medium text-sm">{key.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <code>{key.key_prefix}...</code>
                            {key.last_used_at && (
                              <span>
                                Último uso: {new Date(key.last_used_at).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleRevokeKey(key.id)}
                          disabled={!key.is_active}
                        >
                          {key.is_active ? 'Revogar' : 'Revogada'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* CUA Integration Guide */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <h3 className="font-medium text-sm">Como usar com CUA</h3>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>1.</strong> Crie uma chave API acima</p>
                  <p><strong>2.</strong> Configure seu CUA para chamar:</p>
                  <code className="block p-2 bg-background rounded mt-1">
                    GET /api/cua/pending-clips<br/>
                    Header: X-API-Key: sua_chave
                  </code>
                  <p className="mt-2"><strong>3.</strong> O CUA receberá clips pendentes com instruções de postagem</p>
                  <p><strong>4.</strong> Após postar, chame:</p>
                  <code className="block p-2 bg-background rounded mt-1">
                    POST /api/cua/mark-posted<br/>
                    {`{ "clip_id": "...", "platform": "youtube_shorts", "success": true }`}
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
